import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Footer from "../../../customComponents/Footer";
import "../UserAgreement/user-agreement.css";

const tocItems = [
  { num: 1, title: "Overview" },
  { num: 2, title: "How to delete your account" },
  { num: 3, title: "Conditions before deletion" },
  { num: 4, title: "What happens after deletion" },
  { num: 5, title: "Data retention" },
  { num: 6, title: "Need help" },
];

const DeleteAccount = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>Delete Account – Arab Global Crypto Exchange (AGCEX)</title>
        <meta
          name="description"
          content="How to delete or close your Arab Global Crypto Exchange account, including eligibility, what is removed, and how long records are retained."
        />
        <meta
          name="keywords"
          content="AGCEX delete account, close account, Arab Global crypto exchange account deletion, UAE CMA"
        />
      </Helmet>

      <div className="hero-banner-style top-section-gap user_agreement">
        <section className="faq_sec">
          <div className="container">
            <div className="row align-items-center justify-content-center">
              <div className="col-lg-8 col-md-12">
                <div className="section-title">
                  <h1 className="text-gradient text-center">Delete Account</h1>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="agcex-ua-page">
          <div className="container">
            <div className="agcex-ua-hero">
              <p className="agcex-ua-subtitle">Arab Global Crypto Exchange</p>
              <p className="agcex-ua-company">Arab Global Virtual Assets Services LLC SPC</p>
              <div className="agcex-ua-meta">
                <span><i className="ri-calendar-line" /> Last Updated: 19 August 2026</span>
                <span><i className="ri-file-text-line" /> Account closure &amp; deletion</span>
                <span><i className="ri-map-pin-line" /> Abu Dhabi, United Arab Emirates</span>
              </div>
            </div>

            <div className="agcex-ua-card">
              <div className="agcex-ua-toc">
                <h3>Contents</h3>
                <div className="agcex-ua-toc-grid">
                  {tocItems.map((item) => (
                    <a
                      key={item.num}
                      href={`#delete-${item.num}`}
                      className="agcex-ua-toc-item"
                      onClick={(e) => scrollToSection(e, `delete-${item.num}`)}
                    >
                      <span className="agcex-ua-toc-number">{item.num}.</span>
                      {item.title}
                    </a>
                  ))}
                </div>
              </div>

              <div id="delete-1" className="agcex-ua-section">
                <h2>
                  <span className="agcex-ua-section-number">1</span> Overview
                </h2>
                <p>
                  You may request permanent closure (deletion) of your Trading Account on Arab Global Crypto Exchange. Account deletion is
                  final and cannot be reversed. This page explains how to submit a request, when the Company may accept it, and what happens
                  to your assets, access, and personal data afterwards.
                </p>
                <p>
                  Permanent deletion is different from temporarily disabling your account. If you only want to block access for a period of
                  time, use <strong>Disable Account</strong> in Security settings instead of closing the account.
                </p>
              </div>

              <div id="delete-2" className="agcex-ua-section">
                <h2>
                  <span className="agcex-ua-section-number">2</span> How to delete your account
                </h2>
                <p>Signed-in users can complete deletion from the website or the App:</p>
                <ol>
                  <li>Log in to your Arab Global Crypto Exchange account.</li>
                  <li>
                    Open <strong>Profile</strong> &rarr; <strong>Security</strong> &rarr; <strong>Close Account</strong>.
                  </li>
                  <li>Review the data-retention notice and confirm you understand that records will no longer be available to you.</li>
                  <li>Select a reason for leaving (for example, you no longer need the account, you are merging accounts, or another reason).</li>
                  <li>Confirm any remaining asset waiver, then complete security verification (passkey, authenticator, email OTP, or SMS OTP).</li>
                </ol>
                <p>
                  If you cannot sign in, email{" "}
                  <a href="mailto:contactus@arabglobal.ae">contactus@arabglobal.ae</a> from the address registered on the Account and request
                  closure. The Company may still require identity verification before acting.
                </p>
              </div>

              <div id="delete-3" className="agcex-ua-section">
                <h2>
                  <span className="agcex-ua-section-number">3</span> Conditions before deletion
                </h2>
                <p>
                  In accordance with the Terms of Use, the Company will consider a closure request where none of the following apply:
                </p>
                <ul>
                  <li>the Account is suspended or restricted;</li>
                  <li>you have any outstanding debt or unsettled obligation to the Company;</li>
                  <li>you hold an open position or an active order, including a pending fiat deposit or withdrawal;</li>
                  <li>you have a pending Digital Asset deposit or withdrawal;</li>
                  <li>you hold a sub-account linked to a credit or leverage facility extended by the Company; or</li>
                  <li>other users have registered on the Platform using your referral.</li>
                </ul>
                <p>
                  Withdraw remaining Digital Assets and Fiat before you request deletion. Any balance left in a closed Account may be
                  forfeited. A later deposit sent to a closed Account may result in permanent loss of funds.
                </p>
              </div>

              <div id="delete-4" className="agcex-ua-section">
                <h2>
                  <span className="agcex-ua-section-number">4</span> What happens after deletion
                </h2>
                <ul>
                  <li>Account closure is final and cannot be reversed.</li>
                  <li>Access to trading, wallets, API keys, and related services is permanently disabled.</li>
                  <li>
                    Your transaction records will no longer be available in the Platform. Export any trading, transaction, or support
                    records you wish to keep before you submit the request.
                  </li>
                  <li>
                    If you later open a new account, identity verification may be restricted for 30 days or longer, and referral rewards or
                    rebates tied to this Account may not carry over.
                  </li>
                  <li>
                    Where an Account is closed due to suspected abuse of a promotional offer, the Company may block any new Account
                    subsequently registered by the same Client.
                  </li>
                </ul>
              </div>

              <div id="delete-5" className="agcex-ua-section">
                <h2>
                  <span className="agcex-ua-section-number">5</span> Data retention
                </h2>
                <p>
                  Closing the Account does not mean all personal data is erased immediately. You consent to the Company retaining
                  transaction records, Account history, and Personal Information for a period of eight (8) years, or such other period as
                  required under Applicable Regulations, in accordance with the Terms of Use and the{" "}
                  <Link to="/privacy_policy">Privacy Policy</Link>.
                </p>
                <p>
                  Retention supports AML/CFT, regulatory reporting, dispute handling, and other legal obligations of Arab Global Virtual
                  Assets Services LLC SPC as a CMA-licensed operator.
                </p>
              </div>

              <div id="delete-6" className="agcex-ua-section">
                <h2>
                  <span className="agcex-ua-section-number">6</span> Need help
                </h2>
                <p>
                  Questions about account deletion may be sent to{" "}
                  <a href="mailto:contactus@arabglobal.ae">contactus@arabglobal.ae</a>. Complaints relating to this process may also be
                  raised under the Company&rsquo;s complaints procedure, without limiting any right you have under Applicable Regulations
                  or before the Capital Market Authority.
                </p>
              </div>

              <div className="agcex-ua-copyright">
                &copy; 2026 Arab Global Virtual Assets Services LLC SPC, trading as Arab Global Crypto Exchange. All rights reserved.
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default DeleteAccount;
